import * as React from "react";

// Frontend permission flags. The server is the source of truth — capabilities
// are resolved from Setting(section: "permissions") and returned in
// /api/v1/sessions/me as user.capabilities. The frontend just reads them.
//
// Keep CAPABILITY_KEYS in sync with Setting::PERMISSION_KEYS (Rails).

export const CAPABILITY_KEYS = [
  "can_approve",
  "can_decide_matches",
  "can_manage_matches",
  "can_resolve_alerts",
  "can_manage_cases",
  "can_manage_assessments",
  "can_manage_service_plans",
  "can_manage_claims",
  "can_manage_elderly_risk",
  "can_write_visits",
  "can_manage_regions",
  "can_update_org_availability",
  "can_respond_referrals",
  "can_manage_organizations",
  "can_manage_managers",
  "can_transfer_managers",
  "can_manage_admins",
  "can_manage_members",
  "can_manage_settings",
  "can_export_regions",
  "can_export_care_managers",
  "can_export_orgs",
  "can_view_reports",
  "can_create_reports",
] as const;

export type CapabilityKey = typeof CAPABILITY_KEYS[number];
export type CapabilityMap = Record<CapabilityKey, boolean>;

// Camel-case alias keys for the legacy call sites (perms.canApprove …).
// Both styles read the same underlying snake_case server payload.
export interface PermissionFlags extends CapabilityMap {
  canManageRegions:       boolean;
  canUpdateOrgAvailability: boolean;
  canRespondReferrals:      boolean;
  canManageOrganizations: boolean;
  canManageManagers:      boolean;
  canTransferManagers:    boolean;
  canManageAdmins:        boolean;
  canManageMembers:       boolean;
  canManageSettings:      boolean;
  canManageCases:         boolean;
  canManageAssessments:   boolean;
  canManageServicePlans:  boolean;
  canManageClaims:        boolean;
  canManageElderlyRisk:   boolean;
  canDecideMatches:       boolean;
  canManageMatches:       boolean;
  canResolveAlerts:       boolean;
  canApprove:             boolean;
  canWriteVisits:         boolean;
  canExportRegions:       boolean;
  canExportCareManagers:  boolean;
  canExportOrgs:          boolean;
  canViewReports:         boolean;
  canCreateReports:       boolean;
}

const ALIAS: Record<keyof Omit<PermissionFlags, CapabilityKey>, CapabilityKey> = {
  canApprove:             "can_approve",
  canDecideMatches:       "can_decide_matches",
  canManageMatches:       "can_manage_matches",
  canResolveAlerts:       "can_resolve_alerts",
  canManageCases:         "can_manage_cases",
  canManageAssessments:   "can_manage_assessments",
  canManageServicePlans:  "can_manage_service_plans",
  canManageClaims:        "can_manage_claims",
  canManageElderlyRisk:   "can_manage_elderly_risk",
  canWriteVisits:         "can_write_visits",
  canManageRegions:       "can_manage_regions",
  canUpdateOrgAvailability: "can_update_org_availability",
  canRespondReferrals:      "can_respond_referrals",
  canManageOrganizations: "can_manage_organizations",
  canManageManagers:      "can_manage_managers",
  canTransferManagers:    "can_transfer_managers",
  canManageAdmins:        "can_manage_admins",
  canManageMembers:       "can_manage_members",
  canManageSettings:      "can_manage_settings",
  canExportRegions:       "can_export_regions",
  canExportCareManagers:  "can_export_care_managers",
  canExportOrgs:          "can_export_orgs",
  canViewReports:         "can_view_reports",
  canCreateReports:       "can_create_reports",
};

function withAliases(caps: Partial<CapabilityMap>): PermissionFlags {
  const base = {} as PermissionFlags;
  for (const k of CAPABILITY_KEYS) base[k] = !!caps[k];
  for (const camel in ALIAS) {
    const snake = ALIAS[camel as keyof typeof ALIAS];
    (base as any)[camel] = !!caps[snake];
  }
  return Object.freeze(base);
}

// Fall-through used during the loading flash and on the canvas/demo screens
// where there is no user session. Showing actions optimistically beats the
// "render → hide → re-render" flicker. The server still re-checks on every
// mutating call, so this is purely a UX choice.
const PERMISSIVE_FLAGS: PermissionFlags = withAliases(
  CAPABILITY_KEYS.reduce((acc, k) => { acc[k] = true; return acc; }, {} as CapabilityMap)
);

export interface PermissionsState extends PermissionFlags {
  loaded: boolean;
  role: string | null;
}

// React hook variant. Takes the same { user, loaded } shape that
// useCurrentUser returns so the screen code reads cleanly:
//
//   const { user, loaded } = useCurrentUser();
//   const perms = usePermissionsFromSession({ user, loaded });
//   {perms.canManageCases && <button>…</button>}
//
// We accept the session as a parameter rather than calling useCurrentUser
// inside the hook so screens that already need the user object don't fire
// the session request twice.
export function usePermissionsFromSession({ user, loaded }: { user: any; loaded: boolean }): PermissionsState {
  const role = user?.role ?? null;
  const caps = user?.capabilities as Partial<CapabilityMap> | undefined;
  return React.useMemo<PermissionsState>(() => {
    if (!loaded) return { ...PERMISSIVE_FLAGS, loaded: false, role: null };
    if (!caps)   return { ...withAliases({}), loaded: true, role };
    return { ...withAliases(caps), loaded: true, role };
  }, [loaded, role, caps]);
}
